import React, { type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { ButtonNew as Button } from "components/buttonNew";
import Input from "components/form/input/Input";
import Portal from "elements/Portal";
import { EmailBuilderFieldMergeTagPicker } from "../settings/EmailBuilderFieldMergeTagPicker";
import type { IEmailBuilderTextLinkPopoverPosition } from "./emailBuilderTextLinkPopoverPosition";
import "./emailBuilderTextLinkPopover.scss";

interface IEmailBuilderTextLinkPopoverProps {
  href: string;
  editingExistingLink: boolean;
  hrefError?: string | null;
  position: IEmailBuilderTextLinkPopoverPosition;
  onHrefChange: (href: string) => void;
  onHrefBlur?: () => void;
  onApply: () => void;
  onCancel: () => void;
}

export function EmailBuilderTextLinkPopover(props: IEmailBuilderTextLinkPopoverProps) {
  const { t } = useTranslation();
  const {
    href,
    editingExistingLink,
    hrefError = null,
    position,
    onHrefChange,
    onHrefBlur,
    onApply,
    onCancel,
  } = props;
  const isRemoveMode = editingExistingLink && href.trim() === "";
  const applyDisabled = !isRemoveMode && href.trim() === "";
  const inputId = "email-builder-text-link-url";
  const errorMessage = hrefError;
  return (
    <Portal isMounted={true} parentId="portal-root">
      <div
        className="email-builder-text-link-popover email-builder-text-link-popover--fixed"
        role="dialog"
        aria-label={t("Link URL")}
        style={{
          left: position.left,
          top: position.top,
          width: position.maxWidth,
          maxWidth: position.maxWidth,
          maxHeight: position.maxHeight,
        }}
        onClick={event => event.stopPropagation()}
        onMouseDown={event => event.stopPropagation()}
        onDoubleClick={event => event.stopPropagation()}
      >
        <div className="email-builder-text-link-popover-body">
          <Input
            id={inputId}
            label={t("URL")}
            labelHidden
            type="text"
            value={href}
            autoFocus
            error={errorMessage !== null}
            helpText={errorMessage ?? undefined}
            onChange={event => onHrefChange(event.target.value)}
            onBlur={() => onHrefBlur?.()}
            onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (!applyDisabled) {
                  onApply();
                }
              }
              if (event.key === "Escape") {
                event.preventDefault();
                onCancel();
              }
            }}
            trailingButtons={[
              <EmailBuilderFieldMergeTagPicker
                key="merge-tags"
                value={href}
                variant="link"
                onInsert={onHrefChange}
              />,
            ]}
          />
          <div className="email-builder-text-link-popover-actions">
            <Button type="default" size="small" onClick={onCancel}>
              {t("Cancel")}
            </Button>
            <Button
              type={isRemoveMode ? "error" : "primary"}
              size="small"
              disabled={applyDisabled}
              onClick={onApply}>
              {isRemoveMode ? t("Remove") : t("Apply")}
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  );
}
